import { dxtradeCandleStream } from '@pages/Trade/Markets/Charts/utils/dxtradeCandleStream'
import { Account } from '@pages/Trade/types'

import { ClientApiClient } from '@utils/clientApi'

import { resolutionToCandleType } from './resolutionMap'
import {
  Datafeed,
  LibrarySymbolInfo,
  OnReadyCallback,
  ResolveCallback,
  ResolveErrorCallback,
} from './tradingview'

export function createDxtradeDatafeed(
  api: ClientApiClient,
  account: Account,
  timezone: string
): Datafeed {
  return {
    onReady: (callback: OnReadyCallback) => {
      callback(configurationData)
    },

    searchSymbols: async (
      userInput: string,
      exchange: string,
      symbolType: string,
      onResultReadyCallback: (result: any[]) => void
    ) => {
      const instruments = await api.getDxtradeInstruments()

      const result = instruments
        .filter((i) => i.symbol?.toLowerCase().includes(userInput.toLowerCase()))
        .map((i) => ({
          symbol: i.symbol!,
          ticker: i.symbol!,
          description: i.description ?? i.symbol!,
          exchange: 'DXTRADE',
          type: 'forex',
        }))

      onResultReadyCallback(result)
    },

    resolveSymbol: async (
      symbolName: string,
      onSymbolResolvedCallback: ResolveCallback,
      onResolveErrorCallback: ResolveErrorCallback
    ) => {
      try {
        const [details, instruments] = await Promise.all([
          api.getDxtradeInstrumentDetails(account.platformOverview.login, symbolName),
          api.getDxtradeInstruments(),
        ])

        if (!details?.length) {
          onResolveErrorCallback('unknown_symbol')
          return
        }
        const instrument = details[0]
        const instrumentInfo = instruments.find((i) => i.symbol === symbolName)

        const pricescale = getPriceScaleFromPrecision(instrumentInfo?.priceIncrement)

        const symbolInfo: LibrarySymbolInfo = {
          ticker: symbolName,
          name: symbolName,
          description: instrument.symbol ?? symbolName,
          type: 'forex',
          exchange: '',
          listed_exchange: '',
          session: '24x7',
          timezone,
          minmov: 1,
          pricescale,
          has_intraday: true,
          has_daily: true,
          supported_resolutions: configurationData.supported_resolutions,
          volume_precision: 2,
          data_status: 'streaming' as const,
        }
        onSymbolResolvedCallback(symbolInfo)
      } catch {
        onResolveErrorCallback('resolve_error')
      }
    },

    getBars: async (symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) => {
      const { from, to } = periodParams

      const candleTypeId = resolutionToCandleType[resolution]
      if (!candleTypeId) {
        onErrorCallback(`Unsupported resolution: ${resolution}`)
        return
      }

      try {
        const candles = await api.getDxtradeCandles([symbolInfo.ticker], candleTypeId, from, to)

        if (!candles?.length) {
          onHistoryCallback([], { noData: true })
          return
        }

        const bars = candles
          .map((c) => {
            const close = c.closingPrice ?? 0
            return {
              time: new Date(c.timestamp).getTime(),
              open: c.openingPrice ?? close,
              high: c.highPrice ?? close,
              low: c.lowPrice ?? close,
              close,
              volume: 0,
            }
          })
          .filter((bar) => bar.close > 0 && !isNaN(bar.time))
          .sort((a, b) => a.time - b.time)

        onHistoryCallback(bars, { noData: false })
      } catch (error) {
        onErrorCallback(error)
      }
    },
    subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscriberUID) {
      dxtradeCandleStream.subscribe(
        subscriberUID,
        symbolInfo.ticker,
        resolution,
        onRealtimeCallback
      )
    },

    unsubscribeBars(subscriberUID) {
      dxtradeCandleStream.unsubscribe(subscriberUID)
    },
  }
}

const configurationData = {
  supported_resolutions: [
    '1',
    '5',
    '15',
    '30',
    '60',
    '120',
    '240',

    'D',
    '1D',

    'W',
    '1W',

    'M',
    '1M',
  ],
  exchanges: [{ value: 'DXTRADE', name: 'DXtrade', desc: 'DXtrade instruments' }],
  symbols_types: [{ name: 'forex', value: 'forex' }],
}
const getPriceScaleFromPrecision = (precision?: number | null): number => {
  if (!precision || precision <= 0) return 100000

  const precisionStr = precision.toString()

  if (!precisionStr.includes('.')) {
    return 1
  }

  const decimalPlaces = precisionStr.split('.')[1]?.length ?? 0
  return 10 ** decimalPlaces
}
